// filter-options.ts
import { getTypeString } from "@adornis/baseql/baseqlTypes.js";
import { baseqlMetaData } from "@adornis/baseql/metadata/metaDataStore.js";
import { resolveFieldDefinition } from "@adornis/baseql/metadata/resolve.js";
import {
  makeUniqueIDForFieldConf
} from "@adornis/field-management/field-configuration.js";
import {
  buildFullFieldManagementOptions
} from "@adornis/field-management/field-management-options.js";
import { BooleanAspectSchema } from "./aspect-schemas/BooleanAspect.js";
import { DateTimeVariations } from "./aspect-schemas/DateTimeAspect.js";
import { EnumAspect, EnumAspectSchema } from "./aspect-schemas/EnumAspect.js";
import { DoesNotExistAspectConfig, ExistsAspectConfig } from "./aspect-schemas/ExistsAspect.js";
import { FloatAspectSchema } from "./aspect-schemas/FloatAspect.js";
import { IntVariations } from "./aspect-schemas/IntAspect.js";
import "./aspect-schemas/ListAspect.js";
import { StringVariations } from "./aspect-schemas/StringAspect.js";
var ensureArray = (val) => !val ? val : Array.isArray(val) ? val : [val];
var defaultAspectMap = {
  String: [...StringVariations, ExistsAspectConfig, DoesNotExistAspectConfig],
  ID: [...StringVariations, ExistsAspectConfig, DoesNotExistAspectConfig],
  Boolean: [BooleanAspectSchema, ExistsAspectConfig, DoesNotExistAspectConfig],
  DateTime: [...DateTimeVariations, ExistsAspectConfig, DoesNotExistAspectConfig],
  Date: [...DateTimeVariations, ExistsAspectConfig, DoesNotExistAspectConfig],
  Float: [FloatAspectSchema, ExistsAspectConfig, DoesNotExistAspectConfig],
  Int: [...IntVariations, ExistsAspectConfig, DoesNotExistAspectConfig],
  Enum: [EnumAspectSchema, ExistsAspectConfig, DoesNotExistAspectConfig],
  default: [ExistsAspectConfig, DoesNotExistAspectConfig]
};
var editorOptionsFullyUpgraded = Symbol("fully upgraded");
function buildFullOptions(options) {
  if (options[editorOptionsFullyUpgraded])
    return options;
  return {
    filter: options.filter,
    searchable: options.searchable ?? true,
    fieldAspectGeneration: buildFullFieldManagementOptions({ ...options.fieldAspectGeneration }),
    getAspectConfigAlternatives(fieldInfo) {
      const generationResult = (fieldInfo.customID ? ensureArray(this.fullIdentifierOverrides?.[fieldInfo.customID]) : void 0) ?? (fieldInfo.fieldName ? ensureArray(this.fullFieldNameOverrides?.[fieldInfo.fieldName]) : void 0) ?? (options.fieldAspectGeneration ? generatePossibleAspectsFromBaseQL(options.fieldAspectGeneration, fieldInfo, this.fullTypeOverrides) : []);
      const optionsResult = options.availableAlternativesForAspect?.(fieldInfo, generationResult);
      const finalResult = optionsResult ? optionsResult : generationResult ?? [];
      return finalResult;
    },
    getAspectConfig(aspect) {
      const shouldBeExactlyOne = this.getAspectConfigAlternatives(aspect).filter(
        (ac) => ac.aspectConstructor._class === aspect._class
      );
      if (shouldBeExactlyOne.length > 1) {
        console.error("too many:", shouldBeExactlyOne);
        throw new Error(`ambiguous result for aspect "${JSON.stringify(aspect)}"`);
      }
      return shouldBeExactlyOne[0];
    },
    possiblyCreateAspect(fieldConf) {
      const existing = options.filter.aspects.find(
        (aspect) => makeUniqueIDForFieldConf(aspect) === makeUniqueIDForFieldConf(fieldConf)
      );
      if (existing)
        return existing;
      const alternatives = this.getAspectConfigAlternatives(fieldConf);
      const filterAspect = alternatives[0];
      if (!filterAspect)
        throw new Error(`no aspect could be created from ${JSON.stringify(fieldConf)}`);
      return constructFilterAspect(filterAspect.aspectConstructor, fieldConf);
    },
    fullFieldNameOverrides: overrideDictionaryToArray(options.overridesForFieldName ?? {}, "onlyForField"),
    fullIdentifierOverrides: overrideDictionaryToArray(options.overridesForIdentifier ?? {}, "onlyForIdentifier"),
    fullTypeOverrides: overrideDictionaryToArray(options.overridesForTypes ?? {}, "onlyForType"),
    onFilterChanged: options.onFilterChanged ?? (() => {
    }),
    [editorOptionsFullyUpgraded]: true
  };
}
function constructFilterAspect(aspect, args) {
  return new aspect(args ?? {});
}
function generatePossibleAspectsFromBaseQL(options, fieldInfo, overrides) {
  if (!options.class)
    throw new Error("cant do this without class");
  const field = getBaseQLFieldForField(options.class, fieldInfo);
  return field ? getTypeFittingAspectsForType(field.type, overrides ?? {}) : [];
}
function getBaseQLFieldForField(entityClass, fieldInfo) {
  const fieldName = fieldInfo.fieldName;
  if (!fieldName)
    return null;
  try {
    return resolveFieldDefinition(entityClass, fieldName);
  } catch (error) {
    if (!error.message.includes("could not resolve field"))
      throw error;
    return null;
  }
}
function getTypeFittingAspectsForType(typeFunc, ...overrides) {
  let aspectMap = defaultAspectMap;
  for (const override of overrides.filter(Boolean))
    aspectMap = { ...aspectMap, ...override };
  const typeName = (() => {
    const name = getTypeString(typeFunc);
    const enumNames = Object.keys(baseqlMetaData.enums);
    if (enumNames.includes(name))
      return "Enum";
    return name;
  })();
  const aspectSchemas = aspectMap[typeName] ?? [];
  if (typeName === "Enum" && Array.isArray(aspectSchemas))
    for (const aspectSchema of aspectSchemas) {
      if (aspectSchema.aspectConstructor._class === "EnumAspect") {
        aspectSchema.aspectConstructor = class extends EnumAspect {
          constructor(fieldConfiguration) {
            Object.assign(fieldConfiguration, { enumName: getTypeString(typeFunc) });
            super(fieldConfiguration);
          }
        };
      }
    }
  return Array.isArray(aspectSchemas) ? aspectSchemas : [aspectSchemas];
}
function overrideDictionaryToArray(dict, mode) {
  return Object.fromEntries(
    Object.entries(dict).map(([key, value_s]) => {
      if (Array.isArray(value_s)) {
        value_s = value_s.map((val) => {
          return {
            ...val,
            [mode]: key
          };
        });
      } else {
        value_s = {
          ...value_s,
          [mode]: key
        };
      }
      return [key, value_s];
    })
  );
}
export {
  buildFullOptions,
  constructFilterAspect,
  defaultAspectMap,
  editorOptionsFullyUpgraded,
  getBaseQLFieldForField
};
